import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CurrencyDollar,
  ShoppingCart,
  Package,
  Users,
  TrendUp,
  TrendDown,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  WarningCircle,
  ChartBar,
  Gear,
} from '@phosphor-icons/react';
import { MERCHANT_ROUTES, API_ENDPOINTS, apiClient, useAuthStore } from '@dukene/shared';
import RevenueChart from '../components/dashboard/RevenueChart';
import OnboardingChecklist from '../components/dashboard/OnboardingChecklist';

interface DashboardStats {
  totalRevenue: number;
  totalOrders: number;
  totalProducts?: number;
  totalCustomers: number;
  revenueChange: number;
  ordersChange: number;
  productsChange?: number;
  customersChange: number;
  averageOrderValue?: number;
  aovChange?: number;
}

interface RecentOrder {
  id: string;
  orderNumber: string;
  customer?: {
    firstName: string;
    lastName: string;
    name?: string;
  };
  email?: string;
  total: number;
  status: string;
  source?: string;
  createdAt: string;
}

interface LowStockItem {
  id: string;
  product: {
    id: string;
    name: string;
    title?: string;
    sku: string;
  };
  quantity: number;
  lowStockThreshold: number;
}

export default function Dashboard() {
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const [isOnboardingVisible, setIsOnboardingVisible] = useState(true);

  const { data: stats, isLoading: isStatsLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats', activeStoreId],
    queryFn: async () => {
      return apiClient.get<DashboardStats>('/analytics/stats?range=month');
    },
    enabled: !!activeStoreId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: productsData, isLoading: isProductsLoading } = useQuery({
    queryKey: ['products-count', activeStoreId],
    queryFn: async () => {
      return apiClient.get<any>('/products?page=1&limit=1');
    },
    enabled: !!activeStoreId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: recentOrders = [], isLoading: isOrdersLoading } = useQuery<RecentOrder[]>({
    queryKey: ['recent-orders', activeStoreId],
    queryFn: async () => {
      const res = await apiClient.get<any>(
        '/orders?page=1&limit=5&sortBy=createdAt&sortOrder=desc'
      );
      return Array.isArray(res) ? res : res?.data || [];
    },
    enabled: !!activeStoreId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: lowStockProducts, isLoading: isLowStockLoading } = useQuery<LowStockItem[]>({
    queryKey: ['low-stock-products', activeStoreId],
    queryFn: async () => {
      try {
        const res = await apiClient.get<any>('/inventory/low-stock');
        return Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data)
          ? res.data
          : [];
      } catch {
        return [];
      }
    },
    enabled: !!activeStoreId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: connectedProviders = [], isLoading: isProvidersLoading } = useQuery({
    queryKey: ['merchant-channels', activeStoreId],
    queryFn: async () => {
      const res = await apiClient.get<any>(API_ENDPOINTS.MERCHANT.CHANNELS.LIST);
      return res?.channels || (Array.isArray(res) ? res : []);
    },
    enabled: !!activeStoreId,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = isStatsLoading || isProductsLoading || isOrdersLoading || isLowStockLoading || isProvidersLoading;

  const hasActivePayments =
    Array.isArray(connectedProviders) && connectedProviders.some((p: any) => p.isActive);

  const totalProducts = productsData?.pagination?.total || 0;

  const statCards = [
    {
      label: 'Monthly Gross Revenue',
      value: `KES ${(stats?.totalRevenue || 0).toLocaleString()}`,
      change: stats?.revenueChange || 0,
      icon: CurrencyDollar,
      link: MERCHANT_ROUTES.ANALYTICS,
    },
    {
      label: 'Total Orders',
      value: (stats?.totalOrders || 0).toLocaleString(),
      change: stats?.ordersChange || 0,
      icon: ShoppingCart,
      link: MERCHANT_ROUTES.ORDERS,
    },
    {
      label: 'Catalog Products',
      value: totalProducts.toLocaleString(),
      change: 0,
      icon: Package,
      link: MERCHANT_ROUTES.PRODUCTS,
    },
    {
      label: 'Active Customers',
      value: (stats?.totalCustomers || 0).toLocaleString(),
      change: stats?.customersChange || 0,
      icon: Users,
      link: MERCHANT_ROUTES.CUSTOMERS,
    },
  ];

  const getOrderStatusBadge = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'DELIVERED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
          <CheckCircle weight="fill" className="w-3 h-3" />
          Delivered
        </span>
      );
    }
    if (s === 'PENDING') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <Clock weight="fill" className="w-3 h-3" />
          Pending
        </span>
      );
    }
    if (s === 'CANCELLED' || s === 'REFUNDED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
          <XCircle weight="fill" className="w-3 h-3" />
          {s}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#EBF5FA] text-[#006FBB] border border-[#BDE1F4]">
        {s}
      </span>
    );
  };

  return (
    <div className="space-y-5">
      {/* High-Density Polaris Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Store Performance Overview
          </h1>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Monitor real-time sales volume, inventory thresholds, and customer orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={MERCHANT_ROUTES.ANALYTICS}
            className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-[#202223] dark:text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
          >
            <ChartBar weight="fill" className="w-3.5 h-3.5 text-[#006838]" />
            Full Analytics
          </Link>
          <Link
            to={MERCHANT_ROUTES.SETTINGS}
            className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-[#202223] dark:text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
          >
            <Gear weight="fill" className="w-3.5 h-3.5 text-[#5C5F62]" />
            Settings
          </Link>
        </div>
      </div>

      {/* Payment Gateway Alert */}
      {!isOnboardingVisible && !hasActivePayments && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 rounded-xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <WarningCircle weight="fill" className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-xs font-bold text-amber-900 dark:text-amber-200">
                Payment Gateways Inactive
              </h3>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                Your storefront cannot accept online checkouts until at least one payment provider (such as M-Pesa or card) is enabled.
              </p>
            </div>
          </div>
          <Link
            to={MERCHANT_ROUTES.SETTINGS_PAYMENTS}
            className="h-7 px-3 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center shrink-0"
          >
            Configure Payments
          </Link>
        </div>
      )}

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          const isPositive = stat.change >= 0;
          const TrendIcon = isPositive ? TrendUp : TrendDown;

          return (
            <Link
              key={stat.label}
              to={stat.link}
              className="block bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 p-4 rounded-xl shadow-sm hover:border-[#006838] transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
                  {stat.label}
                </span>
                <div className="w-8 h-8 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#006838]">
                  <Icon weight="fill" className="w-4 h-4" />
                </div>
              </div>

              <div className="flex items-baseline justify-between mt-1">
                <p className="text-xl font-bold text-[#202223] dark:text-white font-mono">
                  {isLoading ? (
                    <div className="h-7 w-24 bg-slate-200 dark:bg-slate-700 animate-pulse rounded mt-1"></div>
                  ) : (
                    stat.value
                  )}
                </p>
                {!isLoading && stat.change !== 0 && (
                  <span
                    className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${
                      isPositive ? 'text-[#006838]' : 'text-red-600'
                    }`}
                  >
                    <TrendIcon weight="bold" className="w-3 h-3" />
                    {Math.abs(stat.change)}%
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>

      {/* Main Grid: Orders & Alerts / Checklist */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Recent Orders Table (Takes 2 Columns) */}
        <div className="lg:col-span-2 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
                Recent Customer Orders
              </h2>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                Latest transactions processed across your sales channels
              </p>
            </div>
            <Link
              to={MERCHANT_ROUTES.ORDERS}
              className="text-xs font-semibold text-[#006838] hover:underline inline-flex items-center gap-1"
            >
              View All Orders
              <ArrowRight weight="bold" className="w-3 h-3" />
            </Link>
          </div>

          <div className="flex-1 overflow-x-auto min-h-[200px]">
            {isOrdersLoading ? (
              <div className="flex flex-col gap-3 p-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-10 w-full bg-slate-100 dark:bg-slate-800 animate-pulse rounded-lg"></div>
                ))}
              </div>
            ) : recentOrders && recentOrders.length > 0 ? (
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#E1E4E8] dark:border-slate-800 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400 bg-[#F6F6F7]/30 dark:bg-slate-900/20">
                    <th className="py-2.5 px-4">Order</th>
                    <th className="py-2.5 px-4">Customer</th>
                    <th className="py-2.5 px-4">Status</th>
                    <th className="py-2.5 px-4 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800">
                  {recentOrders.map((order) => {
                    const first = order.customer?.firstName || '';
                    const last = order.customer?.lastName || '';
                    const fullName = `${first} ${last}`.trim();
                    const customerName =
                      fullName ||
                      order.customer?.name ||
                      (order.source === 'POS' ? 'Walk in' : order.email || 'Guest Customer');

                    return (
                      <tr
                        key={order.id}
                        className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                      >
                        <td className="py-3 px-4 font-mono font-bold text-[#202223] dark:text-white">
                          <Link
                            to={`${MERCHANT_ROUTES.ORDERS}/${order.id}`}
                            className="hover:text-[#006838] transition-colors"
                          >
                            {order.orderNumber}
                          </Link>
                        </td>

                        <td className="py-3 px-4 text-[#202223] dark:text-slate-200">
                          {customerName}
                        </td>

                        <td className="py-3 px-4">{getOrderStatusBadge(order.status)}</td>

                        <td className="py-3 px-4 text-right font-mono font-bold text-[#006838]">
                          KES {Number(order.total || 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-14 px-6">
                <ShoppingCart weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-2" />
                <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
                  No Orders Placed Yet
                </h3>
                <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-xs mx-auto">
                  When customers purchase products from your storefront, their orders will appear here.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side Column: Checklist & Low Stock */}
        <div className="space-y-5 flex flex-col">
          <OnboardingChecklist onVisibilityChange={setIsOnboardingVisible} />

          {/* Low Stock Alert Tile */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden flex-1 flex flex-col">
            <div className="p-4 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-[#202223] dark:text-white">
                  Low Stock Watch
                </h2>
                <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                  SKUs approaching reorder limits
                </p>
              </div>
              <Link
                to={MERCHANT_ROUTES.INVENTORY}
                className="text-xs font-semibold text-[#006838] hover:underline inline-flex items-center gap-1"
              >
                Inventory
                <ArrowRight weight="bold" className="w-3 h-3" />
              </Link>
            </div>

            <div className="p-3.5 space-y-2.5 flex-1">
              {lowStockProducts && lowStockProducts.length > 0 ? (
                lowStockProducts.slice(0, 5).map((item) => {
                  if (!item.product) return null;
                  const productName =
                    item.product.title || item.product.name || 'Unnamed Product';
                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2.5 rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-950/20 text-xs"
                    >
                      <div className="min-w-0 pr-2">
                        <p className="font-semibold text-[#202223] dark:text-white truncate">
                          {productName}
                        </p>
                        <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 font-mono mt-0.5">
                          SKU: {item.product.sku}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white">
                          {item.quantity} left
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-10 px-4">
                  <CheckCircle weight="fill" className="w-8 h-8 text-[#006838] mx-auto mb-2" />
                  <p className="text-xs font-semibold text-[#202223] dark:text-white">
                    Inventory Fully Stocked
                  </p>
                  <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                    No items fall below their minimum inventory thresholds.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Performance Chart */}
      <RevenueChart />

      {/* Quick Action Buttons Row */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm">
        <h2 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400 mb-3">
          Quick Shortcuts
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link
            to={MERCHANT_ROUTES.PRODUCTS_NEW}
            className="flex items-center gap-2.5 p-3 rounded-lg border border-[#D2D5D9] dark:border-slate-800 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 transition-colors text-xs font-semibold text-[#202223] dark:text-white"
          >
            <div className="w-7 h-7 rounded-md bg-[#E3F1DF] text-[#006838] flex items-center justify-center shrink-0">
              <Package weight="fill" className="w-4 h-4" />
            </div>
            <span>Add Product</span>
          </Link>

          <Link
            to={MERCHANT_ROUTES.ORDERS}
            className="flex items-center gap-2.5 p-3 rounded-lg border border-[#D2D5D9] dark:border-slate-800 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 transition-colors text-xs font-semibold text-[#202223] dark:text-white"
          >
            <div className="w-7 h-7 rounded-md bg-[#F6F6F7] dark:bg-slate-800 text-[#2C6ECB] flex items-center justify-center shrink-0">
              <ShoppingCart weight="fill" className="w-4 h-4" />
            </div>
            <span>Manage Orders</span>
          </Link>

          <Link
            to={MERCHANT_ROUTES.ANALYTICS}
            className="flex items-center gap-2.5 p-3 rounded-lg border border-[#D2D5D9] dark:border-slate-800 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 transition-colors text-xs font-semibold text-[#202223] dark:text-white"
          >
            <div className="w-7 h-7 rounded-md bg-[#F6F6F7] dark:bg-slate-800 text-[#006838] flex items-center justify-center shrink-0">
              <ChartBar weight="fill" className="w-4 h-4" />
            </div>
            <span>Analytics Suite</span>
          </Link>

          <Link
            to={MERCHANT_ROUTES.SETTINGS}
            className="flex items-center gap-2.5 p-3 rounded-lg border border-[#D2D5D9] dark:border-slate-800 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 transition-colors text-xs font-semibold text-[#202223] dark:text-white"
          >
            <div className="w-7 h-7 rounded-md bg-[#F6F6F7] dark:bg-slate-800 text-[#5C5F62] flex items-center justify-center shrink-0">
              <Gear weight="fill" className="w-4 h-4" />
            </div>
            <span>Store Settings</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
